import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Check, X, Box, Truck, Wrench, Hammer, Monitor, Smartphone, Anchor, Armchair, Package, ArrowLeft, Printer, Settings2, LayoutTemplate } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../utils/api';
import LabelGenerator, { LABEL_SIZES, LabelSize, LabelRenderType, LabelDesign, LabelTheme } from '../../components/assets/LabelGenerator';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../components/ui/utils';

const PrintLabels = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [allAssets, setAllAssets] = useState<any[]>([]);
    const [selectedAssetIds, setSelectedAssetIds] = useState<Set<number>>(new Set());
    const [organizations, setOrganizations] = useState<any[]>([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState<string>('All');
    const [searchQuery, setSearchQuery] = useState('');

    const [selectedSizeId, setSelectedSizeId] = useState<string>(LABEL_SIZES[4].id); // Default 50x28
    const [labelType, setLabelType] = useState<LabelRenderType>('qrcode');
    const [labelDesign, setLabelDesign] = useState<LabelDesign>('standard');
    const [labelTheme, setLabelTheme] = useState<LabelTheme>('default');
    const [printLayout, setPrintLayout] = useState<'auto' | 'a4' | 'letter'>('auto');

    const printContainerRef = useRef<HTMLDivElement>(null);

    const assetTypes = [
        { id: 'All', label: 'All Types', icon: <Package size={14} /> },
        { id: 'Vehicle', label: 'Vehicles', icon: <Truck size={14} /> },
        { id: 'Plant & Equipment', label: 'Plant & Equipment', icon: <Wrench size={14} /> },
        { id: 'Tools', label: 'Tools', icon: <Hammer size={14} /> },
        { id: 'IT Equipment', label: 'IT Equipment', icon: <Monitor size={14} /> },
        { id: 'Mobile Devices', label: 'Mobile Devices', icon: <Smartphone size={14} /> },
        { id: 'Lifting/Slings', label: 'Lifting/Slings', icon: <Anchor size={14} /> },
        { id: 'Furniture', label: 'Furniture', icon: <Armchair size={14} /> },
    ];

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch organization settings
                const orgRes = await api.get('/api/organization');
                const orgs = (orgRes as any).organizations || [];
                setOrganizations(orgs);
                if (orgs.length > 0) {
                    setSelectedCompanyId(orgs[0].id.toString());
                }

                // Fetch all assets
                const assetRes = await api.get('/api/assets');
                const fetchedAssets = assetRes as any[];
                setAllAssets(fetchedAssets);

                // Initial selection
                if (location.state && location.state.assets) {
                    const initialIds = new Set<number>(location.state.assets.map((a: any) => a.id));
                    setSelectedAssetIds(initialIds);
                } else {
                    // Default select top 5 if none provided
                    const initialIds = new Set<number>(fetchedAssets.slice(0, 5).map((a: any) => a.id));
                    setSelectedAssetIds(initialIds);
                }
            } catch (err) {
                console.error("Failed to fetch data", err);
                toast.error("Failed to load assets");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [location.state]);

    const filteredAssets = allAssets.filter((a: any) => {
        const matchesType = typeFilter === 'All' || a.type === typeFilter;
        const matchesSearch = !searchQuery ||
            a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            a.tag.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesType && matchesSearch;
    });

    const selectedAssets = filteredAssets.filter((a: any) => selectedAssetIds.has(a.id));

    const toggleAssetSelection = (id: number) => {
        const newSelection = new Set(selectedAssetIds);
        if (newSelection.has(id)) {
            newSelection.delete(id);
        } else {
            newSelection.add(id);
        }
        setSelectedAssetIds(newSelection);
    };

    const handleSelectAll = () => {
        const newSelection = new Set(selectedAssetIds);
        filteredAssets.forEach((a: any) => newSelection.add(a.id));
        setSelectedAssetIds(newSelection);
    };

    const handleDeselectAll = () => {
        const newSelection = new Set(selectedAssetIds);
        filteredAssets.forEach((a: any) => newSelection.delete(a.id));
        setSelectedAssetIds(newSelection);
    };

    const activeSize = LABEL_SIZES.find(s => s.id === selectedSizeId) || LABEL_SIZES[4];
    const activeCompanyName = organizations.find((o: any) => o.id.toString() === selectedCompanyId)?.companyName || 'Company Name';

    const getPageSpecs = (layout: 'a4' | 'letter') => {
        if (layout === 'a4') {
            return { width: 210, height: 297, margin: 10, gap: 5 };
        }
        return { width: 215.9, height: 279.4, margin: 12.7, gap: 5 }; // Letter margin 0.5in = 12.7mm
    };

    const pagedAssets = useMemo(() => {
        if (printLayout === 'auto') {
            return selectedAssets.map(a => [a]);
        }

        const specs = getPageSpecs(printLayout);
        const useableWidth = specs.width - (specs.margin * 2);
        const useableHeight = specs.height - (specs.margin * 2);

        const cols = Math.floor((useableWidth + specs.gap) / (activeSize.width + specs.gap));
        const rows = Math.floor((useableHeight + specs.gap) / (activeSize.height + specs.gap));

        const perPage = Math.max(1, cols * (rows || 1));
        const chunks = [];
        for (let i = 0; i < selectedAssets.length; i += perPage) {
            chunks.push(selectedAssets.slice(i, i + perPage));
        }
        return chunks;
    }, [selectedAssets, printLayout, activeSize]);

    const handlePrint = () => {
        const style = document.createElement('style');
        style.type = 'text/css';
        style.media = 'print';

        let pageCss = '';
        console.log(printLayout)
        if (printLayout === 'auto') {
            pageCss = `
                @page {
                    size: ${activeSize.width}mm ${activeSize.height}mm;
                    margin: 0;
                }
                html, body {
                    margin: 0 !important;
                    padding: 0 !important;
                    height: auto !important;
                    min-height: 0 !important;
                    background: white !important;
                }
                .print-page {
                    display: flex !important;
                    flex-direction: column !important;
                    align-items: center !important;
                    justify-content: center !important;
                    width: ${activeSize.width}mm !important;
                    height: ${activeSize.height}mm !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    overflow: hidden !important;
                    position: relative !important;
                    box-sizing: border-box !important;
                    background: white !important;
                }
                .print-page:not(:last-child) {
                    page-break-after: always !important;
                    break-after: page !important;
                }
                #print-container {
                    display: block !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    line-height: 0 !important;
                    font-size: 0 !important;
                }
            `;
        } else {
            const specs = getPageSpecs(printLayout);
            pageCss = `
                @page {
                    size: ${printLayout === 'a4' ? 'A4' : 'letter'} portrait;
                    margin: 0;
                }
                .print-page {
                    width: ${specs.width}mm !important;
                    height: ${specs.height}mm !important;
                    padding: ${specs.margin}mm !important;
                    margin: 0 !important;
                    display: flex !important;
                    flex-wrap: wrap !important;
                    gap: ${specs.gap}mm !important;
                    align-content: flex-start !important;
                    break-after: page;
                    page-break-after: always;
                    box-shadow: none !important;
                    border: none !important;
                    background: white !important;
                    box-sizing: border-box !important;
                    position: relative !important;
                }
                #print-container {
                    display: block !important;
                }
            `;
        }

        style.innerHTML = `
            ${pageCss}
            @media print {
                .print-hidden-sibling {
                    display: none !important;
                }
                .print-ancestor {
                    margin: 0 !important;
                    padding: 0 !important;
                    border: none !important;
                    background: transparent !important;
                    box-shadow: none !important;
                    width: 100% !important;
                    max-width: none !important;
                    height: auto !important;
                    min-height: 0 !important;
                    max-height: none !important;
                    position: static !important;
                    transform: none !important;
                    overflow: visible !important;
                    display: block !important;
                }
                #print-container {
                    margin: 0 !important;
                    padding: 0 !important;
                    width: 100% !important;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                .label-item-wrap {
                    box-shadow: none !important;
                    border: none !important;
                }
                .print-page {
                    background: white !important;
                }
            }
        `;
        document.head.appendChild(style);

        const container = document.getElementById('print-container');
        if (!container) return;

        const modifiedElements: { el: HTMLElement; type: 'ancestor' | 'sibling' }[] = [];
        let currentElement: HTMLElement | null = container;

        while (currentElement && currentElement !== document.body) {
            const parentEl: HTMLElement | null = currentElement.parentElement;
            if (parentEl) {
                parentEl.classList.add('print-ancestor');
                modifiedElements.push({ el: parentEl, type: 'ancestor' });

                for (let i = 0; i < parentEl.children.length; i++) {
                    const child = parentEl.children[i] as HTMLElement;
                    if (child !== currentElement && child.tagName !== 'STYLE' && child.tagName !== 'SCRIPT') {
                        child.classList.add('print-hidden-sibling');
                        modifiedElements.push({ el: child, type: 'sibling' });
                    }
                }
            }
            currentElement = parentEl;
        }

        document.documentElement.classList.add('print-ancestor');
        modifiedElements.push({ el: document.documentElement, type: 'ancestor' });

        window.print();

        setTimeout(() => {
            document.head.removeChild(style);
            modifiedElements.forEach(({ el, type }) => {
                if (type === 'ancestor') el.classList.remove('print-ancestor');
                if (type === 'sibling') el.classList.remove('print-hidden-sibling');
            });
        }, 1000);
    };

    if (loading) {
        return <div className="p-8">Loading print journey...</div>;
    }

    if (allAssets.length === 0 && !loading) {
        return (
            <div className="p-8 pb-32 max-w-7xl mx-auto space-y-6">
                <Button variant="ghost" className="-ml-4 text-gray-500 flex items-center gap-2" onClick={() => navigate(-1)}>
                    <ArrowLeft size={16} />
                    Back to Assets
                </Button>
                <div className="bg-white p-12 text-center rounded-2xl border">
                    <p className="text-gray-500">No assets found in the system.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-8 pb-32 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-300">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                    <Button variant="ghost" className="-ml-4 text-gray-500 flex items-center gap-2" onClick={() => navigate(-1)}>
                        <ArrowLeft size={16} />
                        Back to Assets
                    </Button>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900 mt-2">Print Asset Labels</h1>
                    <p className="text-gray-500 font-medium">
                        {selectedAssets.length === selectedAssetIds.size ? (
                            `Selected ${selectedAssetIds.size} label${selectedAssetIds.size !== 1 ? 's' : ''} to print`
                        ) : (
                            `Showing ${selectedAssets.length} of ${selectedAssetIds.size} selected labels`
                        )}
                    </p>
                </div>

            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Configuration Sidebar */}
                <div className="lg:col-span-1 space-y-6">
                    {/* Asset Selection Filter */}
                    <div className="bg-white border rounded-2xl p-6 space-y-6 shadow-sm">
                        <div className="space-y-1 border-b pb-4">
                            <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                <Box size={16} />
                                Asset Selection
                            </h3>
                            <p className="text-xs text-gray-500">Filter and choose assets to print.</p>
                        </div>

                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Filter by Type</Label>
                                <Select value={typeFilter} onValueChange={setTypeFilter}>
                                    <SelectTrigger className="w-full bg-gray-50 border-gray-100 rounded-xl h-10">
                                        <SelectValue placeholder="All Types" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {assetTypes.map(type => (
                                            <SelectItem key={type.id} value={type.id}>
                                                <div className="flex items-center gap-2">
                                                    {type.icon}
                                                    <span>{type.label}</span>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Search Name/Tag</Label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                    <Input
                                        placeholder="Search..."
                                        className="pl-9 bg-gray-50 border-gray-100 rounded-xl h-10 text-sm"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="pt-2 border-t space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                        {filteredAssets.length} Matching
                                    </span>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={handleSelectAll}
                                            className="text-[10px] font-bold text-blue-600 hover:underline"
                                        >
                                            Select All
                                        </button>
                                        <button
                                            onClick={handleDeselectAll}
                                            className="text-[10px] font-bold text-gray-400 hover:text-gray-600 hover:underline"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>

                                <div className="max-h-[300px] overflow-y-auto pr-2 space-y-1 custom-scrollbar">
                                    {filteredAssets.map((asset: any) => (
                                        <div
                                            key={asset.id}
                                            className={cn(
                                                "flex items-center gap-2 p-2 rounded-xl border transition-all cursor-pointer group",
                                                selectedAssetIds.has(asset.id)
                                                    ? "bg-blue-50 border-blue-100"
                                                    : "bg-white border-dotted border-gray-100 hover:border-gray-300 hover:bg-gray-50"
                                            )}
                                            onClick={() => toggleAssetSelection(asset.id)}
                                        >
                                            <div className="pointer-events-none">
                                                <Checkbox
                                                    checked={selectedAssetIds.has(asset.id)}
                                                    className="rounded-md border-gray-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 h-4 w-4"
                                                />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className={cn(
                                                    "text-xs font-bold truncate",
                                                    selectedAssetIds.has(asset.id) ? "text-blue-900" : "text-gray-700"
                                                )}>
                                                    {asset.name}
                                                </p>
                                                <p className="text-[9px] font-medium text-gray-400">
                                                    {asset.tag}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                    {filteredAssets.length === 0 && (
                                        <div className="py-8 text-center">
                                            <p className="text-xs text-gray-400">No assets match filters</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white border rounded-2xl p-6 space-y-6 shadow-sm">
                        <div className="space-y-1 border-b pb-4">
                            <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                <Settings2 size={16} />
                                Configuration
                            </h3>
                            <p className="text-xs text-gray-500">Adjust how your labels appear.</p>
                        </div>

                        <div className="space-y-3">
                            <Label className="font-bold text-gray-700">Code Type</Label>
                            <div className="flex bg-gray-100 p-1 rounded-full w-full">
                                <button
                                    className={`flex-1 rounded-full px-4 py-2 text-sm font-bold transition-all ${labelType === 'qrcode'
                                        ? 'bg-white text-slate-900 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    onClick={() => setLabelType('qrcode')}
                                >
                                    QR Code
                                </button>
                                <button
                                    className={`flex-1 rounded-full px-4 py-2 text-sm font-bold transition-all ${labelType === 'barcode'
                                        ? 'bg-white text-slate-900 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    onClick={() => setLabelType('barcode')}
                                >
                                    Barcode
                                </button>
                            </div>
                        </div>

                        {organizations.length > 0 && (
                            <div className="space-y-3">
                                <Label className="font-bold text-gray-700">Company Name</Label>
                                <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select company" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {organizations.map((org: any) => (
                                            <SelectItem key={org.id} value={org.id.toString()}>{org.companyName}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="space-y-3">
                            <Label className="font-bold text-gray-700">Layout Design</Label>
                            <Select value={labelDesign} onValueChange={(val) => setLabelDesign(val as LabelDesign)}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select design" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="standard">Standard</SelectItem>
                                    <SelectItem value="compact-left">Compact (Left Code)</SelectItem>
                                    <SelectItem value="compact-right">Compact (Right Code)</SelectItem>
                                    <SelectItem value="top-code">Code at Top</SelectItem>
                                    <SelectItem value="framed">Framed</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-3">
                            <Label className="font-bold text-gray-700">Theme Color</Label>
                            <Select value={labelTheme} onValueChange={(val) => setLabelTheme(val as LabelTheme)}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select theme" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="default">Default White</SelectItem>
                                    <SelectItem value="safety-yellow">Safety Yellow</SelectItem>
                                    <SelectItem value="construction-orange">Construction Orange</SelectItem>
                                    <SelectItem value="industrial-gray">Industrial Gray</SelectItem>
                                    <SelectItem value="midnight-black">Midnight Black</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-3">
                            <Label className="font-bold text-gray-700">Sheet / Print Format</Label>
                            <Select value={printLayout} onValueChange={(val) => setPrintLayout(val as 'auto' | 'a4' | 'letter')}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select layout" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="auto">Roll / Thermal Printer</SelectItem>
                                    <SelectItem value="a4">A4 Pages</SelectItem>
                                    <SelectItem value="letter">Letter Pages</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500 pt-1">Determine how labels flow when printing.</p>
                        </div>

                        <div className="space-y-3">
                            <Label className="font-bold text-gray-700">Label Size (mm)</Label>
                            <Select value={selectedSizeId} onValueChange={setSelectedSizeId}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select size" />
                                </SelectTrigger>
                                <SelectContent>
                                    {LABEL_SIZES.map(s => (
                                        <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500 pt-1">Make sure these dimensions match your connected label printer.</p>
                        </div>


                    </div>
                </div>

                {/* Preview Area */}
                <div className="lg:col-span-3 sticky top-8 self-start">
                    <div className="bg-gray-200/50 rounded-2xl border-2 border-dashed border-gray-300 min-h-[500px] flex flex-col h-[calc(100vh-12rem)] overflow-hidden">
                        <div className="flex items-center justify-between p-6 bg-white/50 border-b backdrop-blur-sm sticky top-0 z-10">
                            <div className="flex items-center gap-2 text-gray-500">
                                <LayoutTemplate size={16} />
                                <span className="text-sm font-bold uppercase tracking-wider text-gray-400">Print Preview</span>
                            </div>
                            <div className="flex items-center gap-4">
                                {selectedAssets.length < selectedAssetIds.size && (
                                    <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 rounded-full border border-amber-100">
                                        <span className="text-[10px] font-bold text-amber-600 uppercase tracking-widest">
                                            {selectedAssetIds.size - selectedAssets.length} Other{selectedAssetIds.size - selectedAssets.length !== 1 ? 's' : ''} hidden
                                        </span>
                                        <button
                                            onClick={() => {
                                                setTypeFilter('All');
                                                setSearchQuery('');
                                            }}
                                            className="text-[10px] font-bold text-amber-700 hover:underline"
                                        >
                                            Show All
                                        </button>
                                    </div>
                                )}
                                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-gray-100 px-2 py-1 rounded-md">
                                    {pagedAssets.length} Page{pagedAssets.length !== 1 ? 's' : ''}
                                </div>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-12 custom-scrollbar bg-gray-100/30">
                            <div
                                id="print-container"
                                className={cn(
                                    "flex flex-col items-center",
                                    printLayout === 'auto' ? "gap-6" : "gap-0"
                                )}
                            >
                                {pagedAssets.map((pageAssets, pageIndex) => (
                                    <div
                                        key={`page-${pageIndex}`}
                                        className={cn(
                                            "print-page relative",
                                            printLayout === 'auto'
                                                ? "bg-white shadow-md border rounded-xl p-6 flex flex-col items-center justify-center w-fit"
                                                : "bg-white shadow-2xl mb-12 border border-gray-200"
                                        )}
                                        style={printLayout !== 'auto' ? {
                                            width: `${getPageSpecs(printLayout).width}mm`,
                                            height: `${getPageSpecs(printLayout).height}mm`,
                                            padding: `${getPageSpecs(printLayout).margin}mm`,
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: `${getPageSpecs(printLayout).gap}mm`,
                                            alignContent: 'flex-start',
                                            boxSizing: 'border-box'
                                        } : {}}
                                    >
                                        {/* Page Indicator for A4/Letter */}
                                        {printLayout !== 'auto' && (
                                            <div className="absolute top-2 right-4 text-[10px] font-bold text-gray-300 uppercase tracking-widest print:hidden">
                                                Page {pageIndex + 1}
                                            </div>
                                        )}

                                        {pageAssets.map((asset: any, idx: number) => (
                                            <div
                                                className="label-item-wrap shadow-sm relative group bg-white border border-gray-100 overflow-hidden shrink-0"
                                                key={`label-${asset.id}-${pageIndex}-${idx}`}
                                                style={{
                                                    width: (printLayout === 'auto' && (activeSize.id === '100x150' || activeSize.id === '102x150')) ? `calc(${activeSize.width}mm - 6mm)` : `${activeSize.width}mm`,
                                                    height: (printLayout === 'auto' && (activeSize.id === '100x150' || activeSize.id === '102x150')) ? `calc(${activeSize.height}mm - 6mm)` : `${activeSize.height}mm`,
                                                    border: (printLayout === 'auto' && (activeSize.id === '100x150' || activeSize.id === '102x150')) ? 'none' : undefined
                                                }}
                                            >
                                                <LabelGenerator
                                                    asset={asset}
                                                    companyName={activeCompanyName}
                                                    size={activeSize}
                                                    type={labelType}
                                                    design={labelDesign}
                                                    theme={labelTheme}
                                                    hasPadding={printLayout === 'auto' && (activeSize.id === '100x150' || activeSize.id === '102x150')}
                                                />
                                                <button
                                                    className="absolute top-1 right-1 w-5 h-5 bg-white shadow-sm border rounded-full items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-100 transition-all opacity-0 group-hover:opacity-100 flex print:hidden"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleAssetSelection(asset.id);
                                                    }}
                                                >
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ))}

                                {selectedAssets.length === 0 && (
                                    <div className="flex-1 flex flex-col items-center justify-center py-20 text-gray-400">
                                        <Package size={48} className="mb-4 opacity-20" />
                                        <p className="font-bold">No assets selected</p>
                                        <p className="text-xs">Select assets from the sidebar to preview labels</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end items-center gap-3 mt-6">
                        <Button className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-6 shadow-lg shadow-slate-200 transition-all active:scale-95" onClick={handlePrint}>
                            <Printer size={16} />
                            Print {selectedAssets.length} Label{selectedAssets.length !== 1 ? 's' : ''}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrintLabels;
